import { UNIT_STATUS_LABELS, UNIT_TYPE_LABELS, type UnitDto } from '@performance-crm/shared';
import { Loader2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { fmt } from '@/lib/format';
import { useUnits } from '@/lib/queries';
import { useDebounced } from '@/lib/useDebounced';

/**
 * Selector de unidades disponibles de un proyecto (fuera de blacklist). La búsqueda por código o tipología se
 * hace en el servidor sobre todo el stock, no sobre la página cargada (ux-comercial-15); `multiple` permite
 * combinar departamento + estacionamiento / depósito en una separación.
 */
export function UnitPicker({
  projectId,
  value,
  onChange,
  multiple = false,
  idPrefix = 'unit',
  extraUnitIds = [],
  lockedIds = [],
}: {
  projectId: string;
  value: string[];
  onChange: (ids: string[], units: UnitDto[]) => void;
  multiple?: boolean;
  idPrefix?: string;
  /**
   * Unidades que deben aparecer aunque no estén disponibles (p. ej. la ya separada por este lead al registrar la
   * venta): se piden por id a la API junto con las ya elegidas, así nunca desaparecen al buscar otra cosa.
   */
  extraUnitIds?: string[];
  /** Unidades marcadas que no se pueden quitar (las separadas por el cliente al vender, Lote 5 · H2 · P6). */
  lockedIds?: string[];
}) {
  const [q, setQ] = useState('');
  const needle = useDebounced(q.trim(), 300);
  const { data, isLoading, isFetching } = useUnits(
    { projectId, available: true, q: needle || undefined },
    { keepPrevious: true },
  );
  const pinnedIds = useMemo(() => [...new Set([...extraUnitIds, ...value])], [extraUnitIds, value]);
  const { data: pinned } = useUnits(
    { projectId, ids: pinnedIds },
    { enabled: pinnedIds.length > 0 },
  );
  // Todas las unidades vistas en este selector: la selección devuelve la unidad completa aunque la búsqueda
  // actual ya no la liste.
  const known = useRef(new Map<string, UnitDto>());
  for (const u of [...(pinned?.items ?? []), ...(data?.items ?? [])]) known.current.set(u.id, u);
  const units = useMemo(() => {
    const base = data?.items ?? [];
    const fresh = new Map((pinned?.items ?? []).map((u) => [u.id, u]));
    const extras = pinnedIds
      .map((id) => fresh.get(id) ?? known.current.get(id))
      .filter((u): u is UnitDto => !!u && !base.some((b) => b.id === u.id));
    return [...extras, ...base];
  }, [data, pinned, pinnedIds]);
  const total = data?.total;
  const toggle = (u: UnitDto) => {
    if (lockedIds.includes(u.id)) return;
    const next = multiple
      ? value.includes(u.id)
        ? value.filter((id) => id !== u.id)
        : [...value, u.id]
      : value.includes(u.id)
        ? []
        : [u.id];
    onChange(
      next,
      next.map((id) => known.current.get(id)).filter((x): x is UnitDto => !!x),
    );
  };
  return (
    <div className="space-y-2">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por código o tipología…"
        aria-label="Buscar unidad"
        id={`${idPrefix}-search`}
      />
      <div className="max-h-48 overflow-y-auto rounded-md border" data-testid="unit-picker">
        {isLoading && <p className="text-muted-foreground p-3 text-sm">Cargando stock…</p>}
        {!isLoading && units.length === 0 && (
          <p className="text-muted-foreground p-3 text-sm">
            {isFetching
              ? 'Buscando…'
              : needle
                ? `No hay unidades disponibles que coincidan con «${needle}» en este proyecto.`
                : 'No hay unidades disponibles en este proyecto.'}
          </p>
        )}
        {units.map((u) => {
          const checked = value.includes(u.id);
          const locked = lockedIds.includes(u.id);
          const unavailable = u.commercialStatus !== 'DISPONIBLE' || u.isBlacklisted;
          return (
            <label
              key={u.id}
              className={`hover:bg-accent flex items-center gap-2 border-b px-2 py-1.5 text-sm last:border-0 ${locked ? 'cursor-default' : 'cursor-pointer'} ${checked ? 'bg-primary/10' : ''}`}
              data-locked={locked ? 'true' : undefined}
            >
              <input
                type={multiple ? 'checkbox' : 'radio'}
                name={`${idPrefix}-unit`}
                checked={checked}
                disabled={locked}
                onChange={() => toggle(u)}
                className="h-4 w-4"
              />
              <span className="font-medium">{u.code}</span>
              <span className="text-muted-foreground text-xs">
                {UNIT_TYPE_LABELS[u.unitType] ?? u.unitType}
                {u.typology ? ` · ${u.typology}` : ''}
                {u.areaTotal ? ` · ${Number(u.areaTotal)} m²` : ''}
                {u.subdivisionName ? ` · ${u.subdivisionName}` : ''}
                {unavailable ? ` · ${UNIT_STATUS_LABELS[u.commercialStatus]}` : ''}
                {locked ? ' · separada por el cliente' : ''}
              </span>
              <span className="ml-auto text-xs">
                {fmt.money(u.priceSale ?? u.priceList, u.currency)}
              </span>
            </label>
          );
        })}
      </div>
      {total !== undefined && data && total > data.items.length && (
        <p
          className="text-muted-foreground flex items-center gap-1 text-xs"
          data-testid="unit-picker-more"
        >
          {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
          Mostrando {fmt.number(data.items.length)} de {fmt.number(total)} disponibles: escribe el
          código o la tipología para encontrar las demás.
        </p>
      )}
    </div>
  );
}
